import classNames from 'classnames'

import useOnClickOutside from 'common/hooks/useOnClickOutside'
import { FC, ReactNode, useRef, useState, useEffect, cloneElement } from 'react'

export type DropdownType = {
  children?: ReactNode
  className?: string
  dropdownClassName?: string
  dropdownContent: ReactNode | ReactNode[]
  icon?: ReactNode
  show: boolean
  position?: 'right'
  hidden?: boolean
  setShow: (show: boolean) => void
}

const Dropdown: FC<DropdownType> = ({
  children,
  className,
  dropdownClassName,
  dropdownContent,
  hidden,
  position,
  setShow,
  show,
}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)

  const isArrayContent = Array.isArray(dropdownContent)

  const closeDropdown = () => {
    setHighlightedIndex(null)
    setShow(false)
  }

  const handleItemClick = (itemOnClick?: () => void) => {
    itemOnClick?.()
    closeDropdown()
  }

  useOnClickOutside(
    ref,
    () => {
      if (show) closeDropdown()
    },
    'mousedown',
  )

  // Keyboard navigation for array-based dropdown items
  useEffect(() => {
    if (!isArrayContent || dropdownContent.length === 0) return

    const lastIndex = dropdownContent.length - 1

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        setHighlightedIndex((prev) =>
          prev === null || prev === lastIndex ? 0 : prev + 1,
        )
      } else if (event.key === 'ArrowUp') {
        setHighlightedIndex((prev) =>
          prev === null || prev === 0 ? lastIndex : prev - 1,
        )
      } else if (event.key === 'Enter' && highlightedIndex !== null) {
        const itemOnClick = (dropdownContent as React.ReactElement[])[
          highlightedIndex
        ]?.props?.onClick
        handleItemClick(itemOnClick)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [dropdownContent, highlightedIndex])

  // Keep highlighted item scrolled into view
  useEffect(() => {
    if (!ref.current || highlightedIndex === null) return

    const container = ref.current.querySelector(
      '.dropdown-menu',
    ) as HTMLElement | null
    const item = ref.current.querySelectorAll('.dropdown-item')[
      highlightedIndex
    ] as HTMLElement | null

    if (!container || !item) return

    const containerRect = container.getBoundingClientRect()
    const itemRect = item.getBoundingClientRect()

    if (itemRect.bottom > containerRect.bottom) {
      container.scrollTop =
        item.offsetTop + item.offsetHeight - container.offsetHeight
    } else if (itemRect.top < containerRect.top) {
      container.scrollTop = item.offsetTop
    }
  }, [highlightedIndex])

  const renderDropdownContent = () => {
    if (!isArrayContent) return dropdownContent

    return dropdownContent.filter(Boolean).map((item, index) => {
      const element = item as React.ReactElement
      return cloneElement(element, {
        className: classNames(element.props.className, {
          active: index === highlightedIndex,
        }),
        key: index,
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation()
          handleItemClick(element.props?.onClick)
        },
      })
    })
  }

  return (
    <div
      ref={ref}
      className={classNames('dropdown position-relative', className)}
      aria-expanded={show}
    >
      {children}
      {!hidden && (
        <div
          style={{ minWidth: '100%' }}
          className={show ? 'py-1 position-absolute' : ''}
        >
          <div
            className={classNames(
              'dropdown-menu p-0',
              { 'right-0': position === 'right', show },
              dropdownClassName,
            )}
            aria-labelledby='navbarDropdown'
          >
            {renderDropdownContent()}
          </div>
        </div>
      )}
    </div>
  )
}

export default Dropdown
